import { initSlice } from 'aa_common/front-end/zustand'
import { AxiosResponse } from 'axios'

import { tryCallApi } from '../helpers'
import { FeatureFlagsService, PresenterEvaluateBatchFeatureFlagResponse } from '../open-api'

export enum FeatureFlagNames {
  MigrationMasterTag = 'migration-master-tag',
  MigrationMasterUnit = 'migration-master-unit',
  MigrationMasterCause = 'migration-master-cause',
  MigrationMasterArea = 'migration-master-area',
  MigrationMasterAssetCategory = 'migration-master-asset-category',
  MigrationMasterSubmitDestination = 'migration-master-submit-destination',
  MigrationMasterExcise = 'migration-master-excise',
  MigrationMasterDepartment = 'migration-master-department',
  AutoJournalSettingError = '17421-auto-journal-setting-error',
  RemarkInJournalSettings = '17779-remark-in-journal-settings',
  PartialImpairment = '16086-register-partial-impairment',
  AssetCategoryJudgmentAI = '18054_asset_category_judgment_ai',
}

export type FeatureNameType =
  | FeatureFlagNames.MigrationMasterTag
  | FeatureFlagNames.MigrationMasterUnit
  | FeatureFlagNames.MigrationMasterCause
  | FeatureFlagNames.MigrationMasterArea
  | FeatureFlagNames.MigrationMasterAssetCategory
  | FeatureFlagNames.MigrationMasterSubmitDestination
  | FeatureFlagNames.MigrationMasterExcise
  | FeatureFlagNames.MigrationMasterDepartment
  | FeatureFlagNames.AutoJournalSettingError
  | FeatureFlagNames.RemarkInJournalSettings
  | FeatureFlagNames.PartialImpairment
  | FeatureFlagNames.AssetCategoryJudgmentAI

const initState: Partial<Record<FeatureNameType, boolean>> = {}

export const openFeatureSlice = initSlice('open-features', initState)

export const featureList: FeatureType[] = [
  {
    featureName: FeatureFlagNames.MigrationMasterTag,
    defaultValue: false,
    onProduction: true,
  },
  {
    featureName: FeatureFlagNames.MigrationMasterUnit,
    defaultValue: false,
    onProduction: true,
  },
  {
    featureName: FeatureFlagNames.MigrationMasterCause,
    defaultValue: false,
    onProduction: false,
  },
  {
    featureName: FeatureFlagNames.MigrationMasterArea,
    defaultValue: false,
    onProduction: false,
  },
  {
    featureName: FeatureFlagNames.MigrationMasterAssetCategory,
    defaultValue: false,
    onProduction: false,
  },
  {
    featureName: FeatureFlagNames.MigrationMasterSubmitDestination,
    defaultValue: false,
    onProduction: false,
  },
  {
    featureName: FeatureFlagNames.MigrationMasterExcise,
    defaultValue: false,
    onProduction: false,
  },
  {
    featureName: FeatureFlagNames.MigrationMasterDepartment,
    defaultValue: false,
    onProduction: false,
  },
  {
    featureName: FeatureFlagNames.AutoJournalSettingError,
    defaultValue: false,
    onProduction: true,
  },
  {
    featureName: FeatureFlagNames.RemarkInJournalSettings,
    defaultValue: false,
    onProduction: false,
  },
  {
    featureName: FeatureFlagNames.PartialImpairment,
    defaultValue: false,
    onProduction: false,
  },
  {
    featureName: FeatureFlagNames.AssetCategoryJudgmentAI,
    defaultValue: false,
    onProduction: false,
  },
]

export const isOpenFeatureDevMode = () => {
  const { REACT_APP_ENV, REACT_APP_RELAY_URL } = process.env
  return REACT_APP_ENV === 'local' && !REACT_APP_RELAY_URL
}

export const isTestEnv = () => ['staging', 'beta', 'idev', 'local'].includes(String(process.env.REACT_APP_ENV))

export const checkAllFeatures = async (isPublic?: boolean) => {
  const state: Record<string | number, boolean> = {}

  const featureFlags = featureList.map(({ featureName, defaultValue, onProduction }) => ({
    key: featureName,
    default_value: isTestEnv() ? onProduction || defaultValue : defaultValue,
  }))

  let response: {
    data?: AxiosResponse<PresenterEvaluateBatchFeatureFlagResponse>
    error?: any
  }
  if (!isPublic) {
    response = await tryCallApi(FeatureFlagsService.evaluateBatchCreate)({ bool_flags: featureFlags })
  } else {
    response = await tryCallApi(FeatureFlagsService.featureFlagsEvaluateBatchCreate)({ bool_flags: featureFlags })
  }

  response?.data?.data?.bool_flags?.forEach(({ key, enabled }) => {
    if (key) {
      state[key] = enabled ?? false

      if (isOpenFeatureDevMode()) {
        state[key] = true
      }
    }
  })

  isTestEnv() &&
    featureList.forEach(({ featureName, onProduction, defaultValue }) => {
      state[featureName] = state[featureName] ?? (onProduction || defaultValue)
    })

  openFeatureSlice.setState(state, 'set-open-features')
}

type FeatureType = {
  featureName: FeatureNameType
  defaultValue: any
  onProduction?: boolean
}
